import Vue from 'vue';
import { DirectiveBinding } from 'vue/types/options';
import { UBT } from '@hb/tianqi/lib/ubt.h5';

export interface ModuleExposeData{
  pageId: string;
  categoryId: string;
  moduleId: string;
  contentId: string;
  exposureTimes: string | number;
  businessInfo?: any;
  marketingLabel?: string;
}


interface TargetState {
  /** 解除IntersectionObserver监听 */
  unobserver?: () => void;
  /** 目标元素是否出现在屏幕内 */
  inScreen?: boolean;
  /** 延迟曝光的定时器标识 */
  timer?: number;
}

/** 解除元素曝光监听的清理函数表 */
const _targetStates = new WeakMap<HTMLElement, TargetState>();

const updateTargetState = (target: HTMLElement, partialState: Partial<TargetState>) => {
  _targetStates.set(target, {
    ..._targetStates.get(target),
    ...partialState,
  });
};

const _cleanup = (target: HTMLElement) => {
  const state = _targetStates.get(target);

  if (!state) {
    return;
  }

  const { unobserver } = state;

  if (unobserver) {
    unobserver();
  }

  _targetStates.delete(target);
};
const _exposeOnce = (
  target: HTMLElement,
  duration: number | undefined,
  exposeData: ModuleExposeData,
) => {
  let state = _targetStates.get(target);

  if (!state) {
    return;
  }

  if (typeof duration === 'number') {
    const timer = window.setTimeout(() => {
      state = _targetStates.get(target);

      if (!state) {
        return;
      }

      const { inScreen } = state;

      if (inScreen) {
        _exposeOnce(target, undefined, exposeData);
      }
    }, duration);
    updateTargetState(target, { timer });
  } else {
    UBT.moduleExpose(exposeData);

    // * 在只曝光一次后执行清理回收
    _cleanup(target);
  }
};

export interface ModuleExposeOptions {
  exposeData: ModuleExposeData;
  duration?: number;
  observerOptions?: ConstructorParameters<typeof IntersectionObserver>[1]
}

/**
 * 曝光埋点，可挂载到组件mounted
  Expose.ONCE使用的是dataset.expose_init进行手动标注是否已上报过。
  需使用返回函数手动注销曝光监听。
 * @param options
 * @returns 注销函数
 */
export const moduleExpose = ({
  exposeData,
  target,
  duration,
  observerOptions,
}: ModuleExposeOptions & { target: HTMLElement; }) => {
  const expOption = {
    businessInfo: {},
    ...exposeData,
  };

  if (!exposeData) {
    throw new Error('未传入埋点对象：exposeOption');
  }

  if (!target) {
    throw new Error('未传入监控对象：target');
  }

  let _exposeOb: IntersectionObserver | null = new IntersectionObserver((entries) => {
    entries.forEach((item) => {
      const {
        isIntersecting,
        target: oTarget,
      } = item;

      if (oTarget instanceof HTMLElement) {
        if (expOption.exposureTimes === 1) {
          updateTargetState(oTarget, { inScreen: isIntersecting });

          if (isIntersecting) {
            _exposeOnce(oTarget, duration, expOption);
          } else {
            // * 当元素移出屏幕时，删除定时器
            const state = _targetStates.get(oTarget);

            if (state?.timer) {
              window.clearTimeout(state.timer);
              updateTargetState(oTarget, { timer: undefined });
            }
          }
        } else if (isIntersecting) {
          UBT.moduleExpose(expOption);
        }
      }
    });
  }, observerOptions);
  _exposeOb.observe(target);

  // * 清理回收
  const exposeUnobserver = () => {
    _exposeOb?.unobserve(target);
    _exposeOb = null;
  };
  updateTargetState(target, { unobserver: exposeUnobserver });
  return exposeUnobserver;
};

Vue.directive('node-expose', {
  bind(el: HTMLElement, binding: DirectiveBinding) {
    if (!binding.value) {
      return;
    }

    moduleExpose({
      target: el,
      ...binding.value,
    });
  },
  unbind(el) {
    _cleanup(el);
  },
});
